import { useCallback, useEffect, useRef, useState } from "react";
import {
  useAppDispatch,
  useAppSelector,
  useActiveAnalysis,
  useActiveTabId,
  useActiveUi,
  useCan,
} from "@/state/hooks";
import { CAP } from "@/api/capabilities";
import {
  setAnalysisEnd,
  setAnalysisStart,
  setTimeframe,
  setTradingPanelOpen,
  toggleOneClickPanel,
  clearAllDrawings,
} from "@/state/slices/uiSlice";
import {
  runAnalysis,
  clearAnalysis,
  startLiveAnalysis,
  startLiveWithPrefetch,
  stopLiveAnalysis,
} from "@/state/slices/analysisSlice";
import { PositionsPanel } from "@/components/PositionsPanel/PositionsPanel";
import { ManagementDashboard } from "@/components/ManagementDashboard/ManagementDashboard";
import { useManagementViews } from "@/components/ManagementDashboard/managementViews";
import { TraderAccess } from "@/components/TraderAccess/TraderAccess";
import { ReportsViewer } from "@/components/ReportsViewer/ReportsViewer";
import { TerminalPanel } from "@/components/Terminal/TerminalPanel";
import { DateRangePicker } from "@/components/DateTimePicker/DateRangePicker";
import { analysisTimezone, nowDateTimeLocal } from "@/utils/chartTime";
import { SymbolPicker } from "./SymbolPicker";
import { BacktestMenu } from "./BacktestMenu";
import type { Timeframe } from "@/api/types";
import { SIGNAL_TAB_ID } from "@/state/slices/signalSlice";
import { STRATEGIES_TAB_ID } from "@/state/slices/strategiesSlice";
// Timeframe pill labels come from the single source of truth so they
// never drift from the seconds/HTF metadata used elsewhere.
import { TIMEFRAMES } from "@/utils/timeframe";
import { minTimeframeFor } from "@/utils/symbolMeta";
import styles from "./Header.module.css";
import termStyles from "@/components/Terminal/TerminalPanel.module.css";

/** Coach mark shown once per device, pointing at the trading-account login. */

export function Header() {
  const dispatch = useAppDispatch();
  const tabId = useActiveTabId();
  const { symbol, timeframe, analysisStart, analysisEnd } = useActiveUi();
  // When the full-area signal chart is open it owns the view, so the
  // timeframe control targets IT (the signal tab) instead of the real tab
  // behind it. Only the timeframe is redirected — symbol / analysis stay
  // bound to the user's own tab, since the signal chart is a read-only
  // XAUUSD reference the user shouldn't repoint.
  const signalActive = useAppSelector(
    (s) => s.signal.enabled && s.ui.signalChartOpen,
  );
  const signalTf = useAppSelector((s) => s.ui.byTab[SIGNAL_TAB_ID]?.timeframe);
  const signalSymbol = useAppSelector((s) => s.signal.symbol);
  // The Strategies view is the same kind of full-area reference chart, so the
  // timeframe control drives it while it is open.
  const strategiesActive = useAppSelector(
    (s) => s.strategies.enabled && s.strategies.viewOpen,
  );
  const strategiesTf = useAppSelector((s) => s.ui.byTab[STRATEGIES_TAB_ID]?.timeframe);
  const strategiesSymbol = useAppSelector((s) => s.strategies.symbol);
  const tfTabId = strategiesActive ? STRATEGIES_TAB_ID : signalActive ? SIGNAL_TAB_ID : tabId;
  const tfValue = strategiesActive
    ? strategiesTf ?? timeframe
    : signalActive
      ? signalTf ?? timeframe
      : timeframe;
  // Timeframes finer than the symbol's source can produce have no data at all;
  // greyed out rather than silently blank. "" = no floor, so nothing disabled.
  //
  // The floor must come from the chart this dropdown actually DRIVES, which is
  // the signal chart while it is open. Reading it from `symbol` (the user's own
  // tab) meant parking that tab on GC greyed out every sub-hourly timeframe on
  // the signal chart too — the hourly floor is a property of GC's Databento
  // source, and the signal chart does not use it.
  const tfSymbol = strategiesActive ? strategiesSymbol : signalActive ? signalSymbol : symbol;
  const minTf = minTimeframeFor(tfSymbol);
  const minTfSeconds = minTf
    ? (TIMEFRAMES.find((t) => t.value === minTf)?.seconds ?? 0)
    : 0;
  const {
    status: analysisStatus,
    error: analysisServerError,
    isLive,
    liveTickCount,
  } = useActiveAnalysis();
  // RBAC capability gates (UX only — the backend enforces each with a 403).
  // analysis buttons need analysis.run; the trading chip needs trading.read;
  // the management button shows when any dashboard view is allowed.
  const canAnalyze = useCan(CAP.analysisRun);
  // Directional analysis is tuned for gold (tick_size / price_bin_size);
  // it's XAUUSD-only until those become symbol-aware. Gate the buttons.
  const analysisSupported = symbol === "XAUUSD";
  const canTrade = useCan(CAP.tradingRead);
  // The one-click panel places orders, so its toggle needs trading.write —
  // matches the panel's own mount gate in Chart (oneClickPanelOpen && write).
  const canTradeWrite = useCan(CAP.tradingWrite);
  const oneClickOpen = useAppSelector((s) => s.ui.oneClickPanelOpen);
  const canDashboard = useCan(CAP.dashboardView);
  // Same rule the dashboard itself uses for its views (managementViews.ts).
  const canManagement = useManagementViews().views.length > 0;
  // Phase 5: canTerminal — same cap as ManagementDashboard checks.
  const canTerminal = useCan(CAP.terminalUse);
  // Admin-only research report viewer (reports.view).
  const canReports = useCan(CAP.reportsView);
  const meStatus = useAppSelector((s) => s.me.status);
  const ownerTooltip =
    meStatus === "loading"
      ? "Checking permissions…"
      : "Analysis is restricted to authorised users.";
  // the trading server account chip — owner-only. Pulled from trading slice which is
  // kept fresh by App's useTradingStream (SSE; updates pushed only when
  // the server-side pump observes a change).
  const tradingConnected = useAppSelector((s) => s.trading.connected);
  const tradingAccount = useAppSelector((s) => s.trading.account);
  const tradingOpenCount = useAppSelector((s) => s.trading.positions.length);
  // Trading panel open state lives in Redux so the chart context menu can
  // open it too (not just this chip).
  const positionsOpen = useAppSelector((s) => s.ui.tradingPanelOpen);

  const [validationError, setValidationError] = useState<string | null>(null);
  // Management dashboard (master-only). Local — only opened from here.
  const [adminOpen, setAdminOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  // Connect MT5 / trading-access dialog (every signed-in user sees the
  // button; the SERVER decides whether it offers the request form or the
  // MT5 login form — see TraderAccess).
  const [traderOpen, setTraderOpen] = useState(false);
  // The owner already trades the operator's own terminal, attached at boot —
  // there is nothing for them to log into, so they get the operator face
  // (status only, never the log-in / request dialog). Everyone else —
  // admins and support staff included — trades through a terminal of their
  // own, so they get the request / log-in entry points.
  const isOwner = useAppSelector((s) => s.me.roles.includes("owner"));
  const noTraderAccess = isOwner;
  const tradeBtnRef = useRef<HTMLButtonElement | null>(null);
  // The merged account control's dropdown. Only reachable while connected —
  // with no account there is one thing to do (log in) and a menu to hold it
  // would be ceremony.
  const [acctMenuOpen, setAcctMenuOpen] = useState(false);
  const acctWrapRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!acctMenuOpen) return;
    const onDown = (e: globalThis.MouseEvent) => {
      if (!acctWrapRef.current?.contains(e.target as Node)) {
        setAcctMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setAcctMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [acctMenuOpen]);
  // A disconnect (or a dropped trading server) must not leave a menu hanging
  // over a control that no longer has anything behind it.
  useEffect(() => {
    if (!tradingConnected) setAcctMenuOpen(false);
  }, [tradingConnected]);
  // Phase 5: terminal state lifted here so TerminalPanel stays mounted
  // even when the dashboard closes (WS survives dashboard open/close).
  const [terminalActive, setTerminalActive] = useState(false);
  const [termConnected, setTermConnected] = useState(false);
  const [terminalRefreshKey, setTerminalRefreshKey] = useState(0);

  // Clear local validation error whenever the user edits either field.
  useEffect(() => {
    setValidationError(null);
  }, [analysisStart, analysisEnd]);

  const handleRunAnalysis = () => {
    setValidationError(null);
    if (!analysisStart || !analysisEnd) {
      setValidationError("Pick both Start and End");
      return;
    }
    if (analysisStart >= analysisEnd) {
      setValidationError("Start must be before End");
      return;
    }
    // Combined mode: when End is in the future, prefetch server-side
    // up to "now" and continue live until Stop.
    // Analysis range is interpreted in the chart display zone (UTC) so the
    // picked wall-clock matches the UTC chart axis. See chartTime.ts.
    const tz = analysisTimezone();
    const nowLocal = nowDateTimeLocal();
    if (analysisEnd > nowLocal) {
      dispatch(
        startLiveWithPrefetch({
          tabId,
          request: {
            symbol,
            start_time: analysisStart,
            end_time: nowLocal,
            timezone: tz,
            force_refresh: false,
          },
        }),
      );
      return;
    }
    dispatch(
      runAnalysis({
        tabId,
        request: {
          symbol,
          start_time: analysisStart,
          end_time: analysisEnd,
          timezone: tz,
          force_refresh: false,
        },
      }),
    );
  };

  // Phase 5: close confirm when a terminal session is live.
  const handleAdminClose = useCallback(() => {
    if (
      termConnected &&
      !window.confirm(
        "A terminal session is running on the host. Close the panel? Your " +
          "shell stays alive — Resume to reattach.",
      )
    ) {
      return;
    }
    setAdminOpen(false);
    setTerminalActive(false);
  }, [termConnected]);

  const errorMessage =
    validationError ?? (analysisStatus === "failed" ? analysisServerError : null);

  const handleLiveClick = () => {
    if (isLive) {
      dispatch(stopLiveAnalysis({ tabId }));
    } else {
      // Pure live from now — no server prefetch.
      dispatch(startLiveAnalysis({ tabId, startedAt: Date.now() }));
    }
  };

  return (
    <div className={styles.shell}>
      <header className={styles.header}>
        <div className={styles.brand}>
          <img src="/logo-64.png" alt="" className={styles.brandLogo} />
          Pulsa<span className={styles.brandAccent}>rix</span>
        </div>

        <div className={styles.divider} />

        {/* Symbol + Timeframe — TradingView's first chip group. */}
        <div className={styles.group}>
          <SymbolPicker tabId={tabId} symbol={symbol} />
          <select
            className={styles.tfSelect}
            value={tfValue}
            onChange={(e) =>
              dispatch(
                setTimeframe({ tabId: tfTabId, timeframe: e.target.value as Timeframe }),
              )
            }
            aria-label="Timeframe"
            title={
              TIMEFRAMES.find((t) => t.value === tfValue)?.long ?? "Timeframe"
            }
          >
            {TIMEFRAMES.map((tf) => {
              // A symbol whose source only produces hourly bars (GC) has no
              // data below that. Disabling is clearer than letting the user
              // pick a timeframe that renders an unexplained empty chart.
              const disabled = tf.seconds < minTfSeconds;
              return (
                <option key={tf.value} value={tf.value} disabled={disabled}>
                  {tf.short}
                  {disabled ? " —" : ""}
                </option>
              );
            })}
          </select>
        </div>

        <div className={styles.divider} />

        {/* Analysis time range — one round-trip range calendar (first click =
            start, next = end, times filled after). Both ends are required, so
            no Present option here. Times are UTC (matching the chart axis +
            MT5 data); the badge makes that explicit so a UTC+7 user isn't
            surprised. */}
        <div className={styles.rangeGroup}>
          <DateRangePicker
            from={analysisStart}
            to={analysisEnd}
            onChange={(f, t) => {
              dispatch(setAnalysisStart({ tabId, value: f }));
              if (t) dispatch(setAnalysisEnd({ tabId, value: t }));
            }}
            className={styles.dtInput}
            ariaLabel="Analysis range (UTC)"
            zone="utc"
          />
          <span className={styles.tzBadge} title="Analysis range is in UTC">
            UTC
          </span>
        </div>

        <div className={styles.divider} />

        {/* Action cluster. Run / Live-toggle / Clear; +N live badge
            slips between Live and Clear while running. */}
        <div className={styles.group}>
          <button
            className={styles.btnPrimary}
            onClick={handleRunAnalysis}
            disabled={
              analysisStatus === "loading" ||
              isLive ||
              !canAnalyze ||
              !analysisSupported
            }
            title={
              !canAnalyze
                ? ownerTooltip
                : !analysisSupported
                  ? "Analysis is available for Gold (XAUUSD) only"
                  : isLive
                    ? "Stop live analyze to run a static one"
                    : "Analyse Start → End. Future End triggers combined live mode."
            }
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden>
              <path d="M5 12l4 4L19 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {analysisStatus === "loading" ? "Analysing…" : "Run Analysis"}
          </button>
          <button
            className={`${styles.btnLive} ${isLive ? styles.btnLiveActive : ""}`}
            onClick={handleLiveClick}
            disabled={
              !canAnalyze ||
              !analysisSupported ||
              (!isLive && analysisStatus === "loading")
            }
            title={
              !canAnalyze
                ? ownerTooltip
                : !analysisSupported
                  ? "Analysis is available for Gold (XAUUSD) only"
                  : isLive
                    ? "Stop accumulating; current profile stays on screen"
                    : "Start accumulating tick density from now (stops Live on any other tab)"
            }
          >
            {isLive ? "Stop Live" : "Live Analyze"}
          </button>
          {isLive ? (
            <span
              className={styles.liveCounter}
              title="Ticks processed live since this session started"
            >
              <span className={styles.liveDot} />+{liveTickCount.toLocaleString()}
            </span>
          ) : null}
          <button
            className={styles.btnGhost}
            onClick={() => {
              dispatch(clearAnalysis({ tabId }));
              dispatch(clearAllDrawings({ tabId }));
            }}
            title="Clear the current analysis result and any chart overlay"
          >
            Clear
          </button>
        </div>

        {/* Backtest study results (fib swings, …) → read-only chart overlay.
            Admin/maintenance only (dashboard.view) — matches the mgmt console.
            Backend enforces the same cap; this just hides the control. */}
        {canDashboard ? (
          <>
            <div className={styles.divider} />
            <div className={styles.group}>
              <BacktestMenu tabId={tabId} symbol={symbol} />
            </div>
          </>
        ) : null}

        <div className={styles.spacer} />

        {/* THE account control — one button, not two.

            It used to be a "Trading account" button next to a separate balance
            chip, and the chip said "Offline" whenever no account was attached.
            That reads as a fault ("something is broken") when the truth is
            simply that nobody has logged in yet, and two controls for one
            account is one too many.

            So: no account -> it IS the login button. Account attached -> it
            shows the money (balance + running P&L) and opens a dropdown for
            the two things you can then do — look at the positions/history, or
            manage the account itself.

            Meaning is still the SERVER's to decide (see TraderAccess): a
            non-trader clicking it is how they discover they can ask for
            access, so it is deliberately NOT gated on trading.write.

            Admins never get the login face: the operator's account is already
            attached to the main terminal at boot, so there is nothing for them
            to log into. They keep a plain status face instead.

            Says "trading account" / "broker account", never MT5 — the platform
            the operator happens to run is an implementation detail, and the
            client has never named it to users elsewhere. */}
        {!noTraderAccess || canTrade ? (
          <div className={styles.tradeLoginWrap} ref={acctWrapRef}>
            <button
              ref={tradeBtnRef}
              type="button"
              data-tour="connect-mt5"
              className={`${styles.tradeLoginBtn} ${
                tradingConnected ? styles.tradeLoginOn : styles.tradeLoginCta
              } ${tradingConnected ? styles.acctBtnLive : ""}`}
              aria-haspopup={tradingConnected ? "menu" : undefined}
              aria-expanded={tradingConnected ? acctMenuOpen : undefined}
              onClick={() => {
                if (tradingConnected) {
                  setAcctMenuOpen((v) => !v);
                } else if (!noTraderAccess) {
                  setTraderOpen(true);
                }
              }}
              // An admin with no trading server has nothing to click: no
              // account to log into and no balance to show.
              disabled={noTraderAccess && !tradingConnected}
              title={
                tradingConnected
                  ? `Your trading account — ${tradingOpenCount} open`
                  : noTraderAccess
                    ? "Trading server not connected"
                    : "Log in to your broker account to start trading"
              }
            >
              <span
                className={`${styles.tradeLoginDot} ${
                  tradingConnected ? styles.tradeLoginDotOn : ""
                }`}
                aria-hidden
              />
              {tradingConnected && tradingAccount ? (
                <>
                  <span className={styles.tradingEquity}>
                    {tradingAccount.currency}{" "}
                    {tradingAccount.equity.toFixed(2)}
                  </span>
                  <span
                    className={
                      tradingAccount.profit >= 0
                        ? styles.tradingProfitPositive
                        : styles.tradingProfitNegative
                    }
                  >
                    {tradingAccount.profit >= 0 ? "+" : ""}
                    {tradingAccount.profit.toFixed(2)}
                  </span>
                  {tradingOpenCount > 0 ? (
                    <span className={styles.tradingBadge}>
                      {tradingOpenCount}
                    </span>
                  ) : null}
                  <span className={styles.acctCaret} aria-hidden>
                    ▾
                  </span>
                </>
              ) : noTraderAccess ? (
                "Trading account"
              ) : (
                "Log in to trade"
              )}
            </button>

            {acctMenuOpen && tradingConnected ? (
              <div className={styles.acctMenu} role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className={styles.acctMenuItem}
                  onClick={() => {
                    setAcctMenuOpen(false);
                    dispatch(setTradingPanelOpen(true));
                  }}
                >
                  <span>Positions &amp; history</span>
                  {tradingOpenCount > 0 ? (
                    <span className={styles.acctMenuCount}>
                      {tradingOpenCount}
                    </span>
                  ) : null}
                </button>
                {/* Admins manage the operator terminal elsewhere; there is no
                    per-user account behind this for them. */}
                {!noTraderAccess ? (
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.acctMenuItem}
                    onClick={() => {
                      setAcctMenuOpen(false);
                      setTraderOpen(true);
                    }}
                  >
                    <span>Trading account</span>
                  </button>
                ) : null}
              </div>
            ) : null}

          </div>
        ) : null}

        {/* Management dashboard — dashboard.view or chat.support; it shows
            only the sections the user has capabilities for. */}
        {canManagement ? (
          <button
            type="button"
            className={styles.adminBtn}
            onClick={() => setAdminOpen(true)}
            title="Management dashboard"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              aria-hidden
            >
              <rect x="3" y="3" width="7" height="9" rx="1.5" />
              <rect x="14" y="3" width="7" height="5" rx="1.5" />
              <rect x="14" y="12" width="7" height="9" rx="1.5" />
              <rect x="3" y="16" width="7" height="5" rx="1.5" />
            </svg>
          </button>
        ) : null}

        {/* Research reports — admin only (reports.view). Opens the read-only
            markdown report viewer. */}
        {canReports ? (
          <button
            type="button"
            className={styles.adminBtn}
            onClick={() => setReportsOpen(true)}
            title="Research reports"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              aria-hidden
            >
              <path d="M6 2h9l5 5v15a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1Z" />
              <path d="M14 2v6h6" />
              <path d="M8 13h8M8 17h8M8 9h3" />
            </svg>
          </button>
        ) : null}

        {/* The standalone balance chip lived here. It is now the connected
            face of the account control above — see the note there. */}

        {/* One-click trading toggle — shows/hides the docked SELL/BUY panel on
            the active chart. Needs trading.write (the panel places orders). */}
        {canTradeWrite ? (
          <button
            type="button"
            className={`${styles.oneClickToggle} ${
              oneClickOpen ? styles.oneClickToggleActive : ""
            }`}
            onClick={() => dispatch(toggleOneClickPanel())}
            title={
              oneClickOpen ? "Hide one-click trading" : "Show one-click trading"
            }
            aria-pressed={oneClickOpen}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8Z" strokeLinejoin="round" />
            </svg>
            <span>1-Click</span>
          </button>
        ) : null}
      </header>

      {errorMessage ? (
        <div className={styles.errorBar}>
          <span className={styles.error} role="alert">
            {errorMessage}
          </span>
        </div>
      ) : null}

      <PositionsPanel
        open={positionsOpen}
        onClose={() => dispatch(setTradingPanelOpen(false))}
      />

      <ManagementDashboard
        open={adminOpen}
        onClose={handleAdminClose}
        termConnected={termConnected}
        onTerminalViewChange={setTerminalActive}
        onTerminalRefresh={() => setTerminalRefreshKey((k) => k + 1)}
      />

      <ReportsViewer open={reportsOpen} onClose={() => setReportsOpen(false)} />

      <TraderAccess open={traderOpen} onClose={() => setTraderOpen(false)} />

      {/* Phase 5: TerminalPanel is always mounted here so the WS survives
          dashboard open/close. When the terminal tab is active and the dashboard
          is open, the panel overlays the dashboard's main area (z-index 301 >
          dashboard 300). When not active/visible, it's absolutely-off-screen so
          xterm stays alive in the DOM without affecting layout. */}
      {canTerminal ? (
        <div
          className={termStyles.panelOverlay}
          style={
            terminalActive && adminOpen
              ? undefined
              : { visibility: "hidden", pointerEvents: "none" }
          }
          aria-hidden={!(terminalActive && adminOpen)}
        >
          <TerminalPanel
            active={terminalActive && adminOpen}
            refreshKey={terminalRefreshKey}
            onConnectedChange={setTermConnected}
          />
        </div>
      ) : null}
    </div>
  );
}
